import React from 'react';

const PAGE_DIMENSIONS = {
  a5: { portrait: { width: 148, height: 210 }, landscape: { width: 210, height: 148 } },
};

function blur(val, previewMode) {
  if (!previewMode) return val;
  if (typeof val === 'number') return 'XXX';
  if (typeof val === 'string') return val.replace(/[^\s]/g, 'X');
  return 'XXX';
}

function formatINR(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-IN');
}

function numberToWordsIndian(num) {
  const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
  const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
  let str = '';
  if (num >= 10000000) {
    str += numberToWordsIndian(Math.floor(num / 10000000)) + ' Crore ';
    num = num % 10000000;
  }
  if (num >= 100000) {
    str += numberToWordsIndian(Math.floor(num / 100000)) + ' Lakh ';
    num = num % 100000;
  }
  if (num >= 1000) {
    str += numberToWordsIndian(Math.floor(num / 1000)) + ' Thousand ';
    num = num % 1000;
  }
  if (num >= 100) {
    str += numberToWordsIndian(Math.floor(num / 100)) + ' Hundred ';
    num = num % 100;
  }
  if (num > 0) {
    if (str !== '') str += 'and ';
    if (num < 20) str += a[num] + ' ';
    else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
  }
  return str.replace(/ +/g, ' ').trim();
}

function numToWords(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  if (num === 0) return 'Zero Rupees Only';
  return numberToWordsIndian(num) + ' Rupees Only';
}

function ChallanTemplateA5({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails = {},
  pageSize = 'a5',
  orientation = 'portrait',
  previewMode = false,
}) {
  // Discount logic (matching Sales page)
  const subtotal = billData.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || 0;
  let discount = 0;
  if (billData.discountType === 'percent') {
    discount = subtotal * (parseFloat(billData.discountValue) || 0) / 100;
  } else if (billData.discountType === 'amount') {
    discount = parseFloat(billData.discountValue) || 0;
  }
  const discountedSubtotal = Math.max(0, subtotal - discount);
  
  // GST calculations
  const totalSGST = billData.items?.reduce((sum, item) => sum + ((parseFloat(item.amount) || 0) * ((parseFloat(item.sgst) || 0) / 100)), 0) || 0;
  const totalCGST = billData.items?.reduce((sum, item) => sum + ((parseFloat(item.amount) || 0) * ((parseFloat(item.cgst) || 0) / 100)), 0) || 0;
  const totalIGST = billData.items?.reduce((sum, item) => sum + ((parseFloat(item.amount) || 0) * ((parseFloat(item.gst) || 0) / 100)), 0) || 0;
  
  // For Regular GST, total must be including GST
  const grandTotal = discountedSubtotal + totalSGST + totalCGST + totalIGST;

  // Get page dimensions for A5
  const pageDims = PAGE_DIMENSIONS[pageSize]?.[orientation] || PAGE_DIMENSIONS.a5.portrait;
  const pageStyle = {
    width: '100%',
    maxWidth: `${pageDims.width}mm`,
    minHeight: `${pageDims.height}mm`,
    margin: '0 auto',
    background: 'white',
    boxSizing: 'border-box',
    padding: '8px', // Reduced from 16px for A5
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '6px', // Reduced from 8px
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', // Reduced shadow
  };

  return (
    <div style={pageStyle} className="print:p-0 print:shadow-none print:border-0 relative">
      {/* Header - Compact for A5 */}
      <div className="flex justify-between items-center mb-2">
        <div>
          <h2 className="text-lg font-bold">CHALLAN</h2>
          <div className="text-xs">Challan Number: {previewMode ? 'XXX-XXX/XXXX' : billData.challanNumber || billData.invoiceNumber}</div>
          <div className="text-xs">Challan Date: {previewMode ? 'YYYY-MM-DD' : billData.challanDate || billData.date}</div>
        </div>
        {companyDetails.logoUrl && <img src={companyDetails.logoUrl} alt="Logo" className="h-10 w-10 object-contain" loading="eager" />}
      </div>

      {/* Billed By / Billed To - Compact for A5 */}
      <div className="flex flex-row gap-2 mb-2 seller-buyer-container">
        <div className="seller-buyer-box flex-1">
          <div className="font-bold border-b border-gray-300 pb-1 mb-1 text-sm">Billed By:</div>
          <div className="font-semibold text-gray-900 text-xs">{companyDetails.name || companyDetails.firmName}</div>
          <div className="text-gray-700 text-xs">{companyDetails.address}</div>
          {(companyDetails.gstinType === 'Regular' || companyDetails.gstinType === 'Composition') && <div className="text-gray-700 text-xs">GSTIN: {companyDetails.gstin}</div>}
          <div className="text-gray-700 text-xs">Contact: {companyDetails.contact || companyDetails.contactNumber}</div>
        </div>
        <div className="seller-buyer-box flex-1">
          <div className="font-bold border-b border-gray-300 pb-1 mb-1 text-sm">Bill To:</div>
          <div className="font-semibold text-gray-900 text-xs">{previewMode ? 'XXX Party Name' : partyDetails.name}</div>
          <div className="text-gray-700 text-xs">{previewMode ? 'XXX Address' : partyDetails.address}</div>
          {(companyDetails.gstinType === 'Regular' || companyDetails.gstinType === 'Composition') && <div className="text-gray-700 text-xs">GSTIN: {previewMode ? 'XXGSTINXXXX' : partyDetails.gstin}</div>}
          <div className="text-gray-700 text-xs">Contact: {previewMode ? 'XXXXXXXXXX' : partyDetails.contact}</div>
        </div>
      </div>

      {/* Item Table - Compact for A5 */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border mb-2 text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-1 py-1 text-xs">Sr.</th>
              <th className="border px-1 py-1 text-xs">Item Description</th>
              {(companyDetails.gstinType === 'Regular') && <th className="border px-1 py-1 text-xs">HSN</th>}
              <th className="border px-1 py-1 text-xs">Qty</th>
              <th className="border px-1 py-1 text-xs">Rate</th>
              <th className="border px-1 py-1 text-xs">Amount</th>
              {(companyDetails.gstinType === 'Regular') && <th className="border px-1 py-1 text-xs">GST %</th>}
              <th className="border px-1 py-1 text-xs">Total</th>
            </tr>
          </thead>
          <tbody>
            {(billData.items || []).map((item, idx) => (
              <tr key={idx}>
                <td className="border px-1 py-1 text-center text-xs">{idx + 1}</td>
                <td className="border px-1 py-1 text-xs">
                  {item.description}
                  <div style={{ fontSize: '0.75em', fontStyle: 'italic', color: '#555' }}>
                    {item.nos || item.qty || item.Nos || 1}x{item.length || 1}x{item.height || 1}
                  </div>
                </td>
                {(companyDetails.gstinType === 'Regular') && <td className="border px-1 py-1 text-center text-xs">{item.hsn}</td>}
                <td className="border px-1 py-1 text-center text-xs">
                  {item.qty} {item.unit || item.quantityMeasurement || 'Nos.'}
                </td>
                <td className="border px-1 py-1 text-right text-xs">{item.rate}</td>
                <td className="border px-1 py-1 text-right text-xs">{item.amount}</td>
                {(companyDetails.gstinType === 'Regular') && <td className="border px-1 py-1 text-center text-xs">{item.gst || item.sgst + item.cgst + item.igst}</td>}
                <td className="border px-1 py-1 text-right text-xs">
                  {item.total !== undefined && item.total !== null && item.total !== "" ? item.total : item.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bank & Payment Details + Totals - Compact for A5 */}
      <div className="flex flex-col md:flex-row gap-2 mb-2 bank-totals-flex-row">
        <div className="bank-box" style={{ width: '100%', maxWidth: 'calc(50% - 5mm)' }}>
          <div className="font-bold mb-1 text-sm">Bank & Payment Details:</div>
          <div className="text-xs">{companyDetails.bankName}</div>
          <div className="text-xs">A/C: {companyDetails.bankAccount}</div>
          <div className="text-xs">IFSC: {companyDetails.bankIfsc}</div>
          <div className="text-xs">UPI: {companyDetails.upiId}</div>
          {/* QR Code and Payment Link */}
          <div className="mt-2 flex flex-col items-center">
            {companyDetails.upiQrUrl ? (
              <img src={companyDetails.upiQrUrl} alt="UPI QR" className="w-12 h-12 object-contain mb-2 border" loading="eager" />
            ) : (
              <div className="w-16 h-16 bg-gray-200 flex items-center justify-center text-xs text-gray-500 mb-2">QR</div>
            )}
            {companyDetails.paymentGatewayLink && (
              <a href={companyDetails.paymentGatewayLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">Pay Now</a>
            )}
          </div>
        </div>
        {/* Totals (No GST Table) */}
        <div className="totals-box" style={{ width: '100%', maxWidth: '50%' }}>
          <div className="font-bold mb-1 text-sm">Totals</div>
          <div className="text-xs">Total Amount: ₹{companyDetails.gstinType === 'Regular' ? formatINR(Math.round(grandTotal)) : formatINR(Math.round(discountedSubtotal))}</div>
          <div className="text-xs">Discount: -₹{discount} {billData.discountType === 'percent' ? `(${billData.discountValue || 0}%)` : ''}</div>
          <div className="text-xs">Payable Amount: <b>₹{companyDetails.gstinType === 'Regular' ? formatINR(Math.round(grandTotal)) : formatINR(Math.round(discountedSubtotal))}</b></div>
          <div className="text-xs">In Words: <span className="italic">{numToWords(companyDetails.gstinType === 'Regular' ? Math.round(grandTotal) : Math.round(discountedSubtotal))}</span></div>
        </div>
      </div>

      {/* Terms and Conditions - Compact for A5 */}
      <div className="mb-2">
        <div className="font-bold text-sm">Terms and Conditions:</div>
        <div className="whitespace-pre-line text-xs">{companyDetails.salesTerms || companyDetails.terms}</div>
      </div>

      {/* Disclaimer for Composition Scheme - Compact for A5 */}
      {companyDetails.gstinType === 'Composition' && (
        <div className="text-red-600 font-bold text-xs mb-1">
          Composition taxable person, not eligible to collect tax on supplies
        </div>
      )}

      {/* Footer: Signatures - Compact for A5 */}
      <div className="flex justify-between items-end mt-6 challan-a5-sign-row" style={{ minHeight: '48px' }}>
        <div className="text-xs text-gray-700 flex flex-col items-start justify-end" style={{minHeight:'48px'}}>
          <div className="flex flex-col items-center">
            {/* Receiver Signature image placeholder (if any in future) */}
          </div>
          <span className="mt-1">Receiver Signature</span>
        </div>
        <div className="text-xs text-gray-700 text-right flex flex-col items-end justify-end" style={{minHeight:'48px'}}>
          <div className="flex flex-row items-end gap-2">
            {bankDetails.sealUrl && <img src={bankDetails.sealUrl} alt="Company Seal" loading="eager" crossOrigin="anonymous" style={{height:'24px', maxWidth:'48px', objectFit:'contain', display:'block', printColorAdjust:'exact', WebkitPrintColorAdjust:'exact', visibility:'visible !important', background:'none !important', border:'none !important'}} className="print:visible" />}
            {bankDetails.signUrl && <img src={bankDetails.signUrl} alt="Company Sign" loading="eager" crossOrigin="anonymous" style={{height:'24px', maxWidth:'48px', objectFit:'contain', display:'block', printColorAdjust:'exact', WebkitPrintColorAdjust:'exact', visibility:'visible !important', background:'none !important', border:'none !important'}} className="print:visible" />}
          </div>
          <span className="mt-1">Company Seal & Sign</span>
        </div>
      </div>

      <style>{`
        /* A5 Optimized Styles - Smaller fonts, margins, and spacing */
        .seller-buyer-container {
          display: flex !important;
          flex-direction: row !important;
          gap: 8px !important; /* Reduced from 16px */
          align-items: flex-start !important;
        }
        
        .seller-buyer-box {
          flex: 1 !important;
          border: 1px solid #d1d5db !important; /* Reduced from 2px */
          border-radius: 4px !important; /* Reduced from 8px */
          padding: 8px !important; /* Reduced from 16px */
          background-color: #f9fafb !important;
          min-width: 0 !important;
        }
        
        /* Ensure bank details and totals are always side by side */
        .bank-totals-flex-row {
          display: flex !important;
          flex-direction: row !important;
          gap: 8px !important; /* Reduced from 16px */
          align-items: flex-start !important;
        }
        
        .bank-box {
          flex: 1 !important;
          max-width: 45% !important;
          min-width: 0 !important;
        }
        
        .totals-box {
          flex: 1 !important;
          max-width: 55% !important;
          min-width: 0 !important;
        }

        /* Mobile preview readability (screen only) */
        @media screen and (max-width: 640px) {
          .seller-buyer-container {
            flex-direction: column !important;
            gap: 8px !important;
          }

          .seller-buyer-box {
            width: 100% !important;
          }

          .bank-totals-flex-row {
            flex-direction: column !important;
            gap: 8px !important;
          }

          .bank-box,
          .totals-box {
            width: 100% !important;
            max-width: 100% !important;
          }

          .challan-a5-sign-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
        }
        
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          @page {
            size: ${pageDims.width}mm ${pageDims.height}mm ${orientation};
            margin: 4mm; /* Reduced from 8mm for A5 */
          }
          
          .print\\:p-0 { padding: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: none !important; }
          
          .seller-buyer-container { 
            display: flex !important; 
            flex-direction: row !important; 
            gap: 8px !important; /* Reduced from 16px */
          }
          
          .seller-buyer-box { 
            border: 1px solid #d1d5db !important; /* Reduced from 2px */
            border-radius: 4px !important; /* Reduced from 8px */
            padding: 8px !important; /* Reduced from 16px */
            background-color: #f9fafb !important;
          }
          
          .bank-totals-flex-row { 
            display: flex !important; 
            flex-direction: row !important; 
            gap: 8px !important; /* Reduced from 16px */
          }
          
          .bank-box { 
            max-width: 45% !important; 
            width: 100% !important; 
          }
          
          .totals-box { 
            max-width: 55% !important; 
            width: 100% !important; 
          }
          
          img, .print\\:visible {
            display: block !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            visibility: visible !important;
            max-width: 60px !important; /* Reduced from 80px for A5 */
            max-height: 30px !important; /* Reduced from 40px for A5 */
            background: none !important;
            border: none !important;
          }
        }
        
        /* PDF output uses print CSS via html2pdf.js, so keep print and PDF layouts identical. */
      `}</style>
    </div>
  );
}

export default ChallanTemplateA5;
